import type { QueryClient } from '@tanstack/react-query'
import { BANKING_KEYS, CASH_POSITION_SUMMARIES_KEY } from '@/lib/query-keys'

/**
 * Caches that the transactions of a transaction file import feed — refreshed
 * when an import completes (`transactionImportConfig.invalidateKeys`) and when
 * one is deleted ({@link invalidateAfterTransactionImportDelete}).
 *
 * Imported transactions feed the manual-transaction lists/counts and the
 * banking KPI tiles. They also feed LOC Usage and the facility-category chart,
 * which is less obvious: `process-file-import` inserts into the tenant
 * `transactions` table with `source: 'file_import'` — the very table
 * `get_loc_availability` / `get_loc_transactions` sum for
 * `category = 'LINE OF CREDIT'` and the chart's `facilityCategoryChart` RPC
 * reads. Mirrors what `useUpdateManualTransaction` invalidates for a single-row
 * edit; an import is the same write in bulk.
 *
 * NOT covered here, deliberately: Daily Banking's transactions table
 * reads `useBankTransactions` (`@/hooks/usePlaid`) under
 * `['plaid','transactions']`. Import completion does not invalidate it (the
 * deletion helper below does), and neither does `useUpdateManualTransaction`;
 * those two should be fixed together so the bulk and single-row paths keep
 * mirroring each other.
 */
export const TRANSACTION_IMPORT_DEPENDENT_KEYS = [
  BANKING_KEYS.allManualTransactions,
  BANKING_KEYS.allManualTransactionCounts,
  BANKING_KEYS.allKpi,
  BANKING_KEYS.allLocAvailability,
  BANKING_KEYS.allLocTransactions,
  BANKING_KEYS.allFacilityCategories,
  // Imported rows can land on a credit card's "View transactions" list, and
  // the card table's `has_transactions` decides whether that action shows.
  BANKING_KEYS.allCreditCardTransactions,
  BANKING_KEYS.allCreditCardAvailability,
] as const

/**
 * Invalidate every cache that deleting a transaction file import — the row and
 * all its transactions — can change.
 *
 * On top of {@link TRANSACTION_IMPORT_DEPENDENT_KEYS}: the Import History list
 * and the import's transaction list; Cash Position and Cross-Checking, which
 * read the same `transactions`; and Daily Banking's
 * transaction table with its Category options (legacy `['plaid', ...]` keys,
 * as `invalidateAfterInstitutionRename` does). A deletion is user-initiated
 * and removes rows outright, so the 5-minute staleTime gap accepted for import
 * completion is not accepted here. The import's own by-id entry is dropped
 * rather than refetched: there is nothing left to read.
 *
 * @param queryClient - The active query client
 * @param organizationId - Organization the import belongs to
 * @param fileImportId - The deleted `file_imports.id`
 */
export function invalidateAfterTransactionImportDelete(
  queryClient: QueryClient,
  organizationId: string,
  fileImportId: string,
): void {
  for (const queryKey of TRANSACTION_IMPORT_DEPENDENT_KEYS) {
    queryClient.invalidateQueries({ queryKey })
  }
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImports })
  queryClient.removeQueries({ queryKey: BANKING_KEYS.fileImportById(organizationId, fileImportId) })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allFileImportTransactions })
  queryClient.invalidateQueries({ queryKey: CASH_POSITION_SUMMARIES_KEY })
  queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allCrossChecking })
  queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] })
  queryClient.invalidateQueries({ queryKey: ['plaid', 'categories'] })
}
